import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { Close } from "@/assets";
import { scrollbarClassName } from "./scroll-area";
import { Link } from "react-router";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = ({
    className,
    ...props
}: React.ComponentProps<typeof ToastPrimitives.Viewport>) => (
    <ToastPrimitives.Viewport
        className={cn(
            scrollbarClassName,
            "gap-wide fixed top-0 right-0 z-100 flex max-h-screen w-[420px] max-w-full flex-col-reverse items-stretch overflow-x-hidden p-4",
            className,
        )}
        {...props}
    />
);
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

export const toastVariants = cva(
    "group w-100 border pointer-events-auto relative text-default flex shrink-0 items-center justify-start space-x-default overflow-hidden rounded-default p-wide wrap-anywhere",
    {
        variants: {
            appearance: {
                default: "bg-elevation-surface-sunken-default border-subtle",
                information:
                    "information group bg-information-subtle-default border-information-subtle",
                announcement:
                    "announcement group bg-announcement-subtle-default border-announcement-subtle",
                success:
                    "success group bg-success-subtle-default border-success-subtle",
                warning:
                    "warning group bg-warning-subtle-default border-warning-subtle",
                danger: "danger group bg-danger-default border-danger-subtle",
            },
            isBold: { true: "text-inverse", false: null },
        },
        compoundVariants: [
            {
                appearance: "default",
                isBold: true,
                class: "bg-neutral-bold-default",
            },
            {
                appearance: "information",
                isBold: true,
                class: "bg-information-bold-default",
            },
            {
                appearance: "announcement",
                isBold: true,
                class: "bg-announcement-bold-default",
            },
            {
                appearance: "success",
                isBold: true,
                class: "bg-success-bold-default",
            },
            {
                appearance: "warning",
                isBold: true,
                class: "bg-warning-bold-default",
            },
            {
                appearance: "danger",
                isBold: true,
                class: "bg-danger-bold-default",
            },
        ],
        defaultVariants: { appearance: "default" },
    },
);

export type ToastVariants = VariantProps<typeof toastVariants>;

const Toast = ({
    className,
    appearance = "default",
    isBold = false,
    ...props
}: React.ComponentProps<typeof ToastPrimitives.Root> &
    ToastVariants & { isFlag?: boolean }) => {
    return (
        <ToastPrimitives.Root
            className={cn(
                toastVariants({ appearance, isBold }),
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
                className,
            )}
            {...props}
        />
    );
};
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = ({
    altText,
    ...props
}: Pick<React.ComponentProps<typeof ToastPrimitives.Action>, "altText"> &
    React.ComponentProps<typeof Link>) => (
    <ToastPrimitives.Action asChild altText={altText}>
        <Link
            className="font-body text-default leading-default active:text-link-press cursor-pointer font-bold hover:underline"
            {...props}
        />
    </ToastPrimitives.Action>
);
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = ({
    className,
    ...props
}: React.ComponentProps<typeof ToastPrimitives.Close>) => (
    <ToastPrimitives.Close
        title="close toast"
        className={cn(
            "absolute top-2 right-2 rounded-md p-1 opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100 focus:ring-2 focus:outline-hidden",
            className,
        )}
        toast-close=""
        {...props}
    >
        <Close />
    </ToastPrimitives.Close>
);
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastIcon: React.FC<React.PropsWithChildren> = ({ children }) => (
    <div className="h-6 w-6 shrink-0 self-start">{children}</div>
);

const ToastTitle = ({
    className,
    ...props
}: React.ComponentProps<typeof ToastPrimitives.Title>) => (
    <ToastPrimitives.Title
        className={cn("text-large font-bolder pr-6", className)}
        {...props}
    />
);
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = ({
    className,
    ...props
}: React.ComponentProps<typeof ToastPrimitives.Description>) => (
    <ToastPrimitives.Description
        className={cn("text-default", className)}
        {...props}
    />
);
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentProps<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
    type ToastProps,
    type ToastActionElement,
    ToastProvider,
    ToastViewport,
    Toast,
    ToastTitle,
    ToastIcon,
    ToastDescription,
    ToastClose,
    ToastAction,
};
